import { Divider, Stack } from '@/components/layout';
import { Button, Pagination, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { ListPlayersHeaderProps } from '@/features/player/components/list-player/list-players-header/common';
import {
  useListPlayersHeaderCommon,
  useStyles,
} from '@/features/player/components/list-player/list-players-header/common';
import { SearchBar } from '@/features/player/components/list-player/search-bar';
import { PlayerFilterSheet } from '@/features/player/components/player-filter-sheet';
import { PlayerSortSheet } from '@/features/player/components/player-sort-sheet';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { Activity, memo, PropsWithChildren, useCallback } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { Extrapolation, interpolate, useAnimatedStyle } from 'react-native-reanimated';

const HEADER_HEIGHT_FALLBACK = 64;

export const CreateProfileButton = memo(() => {
  const { scale } = useScaling();
  const styles = useStyles();
  const rootNavigation = useNavigation();

  const onCreateProfile = useCallback(() => {
    rootNavigation.navigate('Auth', { screen: 'MutatePlayer' });
  }, [rootNavigation]);

  return (
    <>
      <Stack paddingX={scale({ phone: 18, tablet: 0 })}>
        <Button onPress={onCreateProfile} style={styles.createProfileButton}>
          <Button.Text style={styles.createProfileButtonText} transform="uppercase">
            {STRINGS.player.listPlayers.createProfileButton}
          </Button.Text>
        </Button>
      </Stack>

      <Divider thickness={StyleSheet.hairlineWidth} color={colors['6']} spacing={scale(20)} />
    </>
  );
});

export const ListPlayersHeaderMobile = ({
  scrollY,
  buttonCreateProfileHeight,
  onHeaderActionLayout,
  onChangeSearchText,
  onClearSearchText,
  searchText,
  paginatedMeta,
  onChangePage,
  children,
  isResultsSummaryVisible,
  extraComponent,
}: PropsWithChildren<ListPlayersHeaderProps>) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const headerHeightFallback = children ? scale(HEADER_HEIGHT_FALLBACK) : 0;
  const {
    totalPages,
    currentPage,
    resultSummary,
    open,
    setOpen,
    level,
    draftLevel,
    setDraftLevel,
    draftLastActive,
    setDraftLastActive,
    filterButtonLabel,
    closeWithoutSave,
    saveAndClose,
    LEVELS,
    lastActiveOptions,
    sortOpen,
    setSortOpen,
    sortKey,
    sortOrder,
    sortDraft,
    setSortDraft,
    sortButtonLabel,
    closeSortWithoutSave,
    saveSortAndClose,
  } = useListPlayersHeaderCommon({ paginatedMeta: paginatedMeta, isResultsSummaryVisible });

  const filterContainerAnimatedStyle = useAnimatedStyle(() => {
    const h =
      buttonCreateProfileHeight.value > 0 ? buttonCreateProfileHeight.value : headerHeightFallback;

    const filterTranslateY = interpolate(scrollY.value, [0, h], [h, 0], {
      extrapolateLeft: Extrapolation.CLAMP,
      extrapolateRight: Extrapolation.CLAMP,
    });

    return { transform: [{ translateY: filterTranslateY }], backgroundColor: colors['2'] };
  });

  const createProfileButtonAnimatedStyle = useAnimatedStyle(() => {
    const h =
      buttonCreateProfileHeight.value > 0 ? buttonCreateProfileHeight.value : headerHeightFallback;

    const filterTranslateY = interpolate(scrollY.value, [0, h], [h, 0], {
      extrapolateLeft: Extrapolation.CLAMP,
      extrapolateRight: Extrapolation.CLAMP,
    });
    const buttonTranslateY = filterTranslateY - h;
    return { transform: [{ translateY: buttonTranslateY }] };
  });

  return (
    <Stack paddingBottom={scale(14)} zIndex={999}>
      <Activity mode={children ? 'visible' : 'hidden'}>
        <Animated.View
          style={[styles.createProfileButtonContainer, createProfileButtonAnimatedStyle]}
          onLayout={e => {
            const h = e.nativeEvent.layout.height;
            buttonCreateProfileHeight.value = h;
            onHeaderActionLayout?.(h);
          }}>
          {children}
        </Animated.View>
      </Activity>

      <Animated.View style={filterContainerAnimatedStyle}>
        <Stack width="100%" gap={scale(8)} paddingX={scale({ phone: 18, tablet: 0 })}>
          <SearchBar
            placeholder={STRINGS.player.listPlayers.searchPlaceholder}
            onChangeText={onChangeSearchText}
            onClear={onClearSearchText}
            value={searchText}
          />

          <Stack direction="row" gap={4}>
            {/* TODO: need refactor, move logic to component sort and filter player */}
            <PlayerFilterSheet
              open={open}
              onOpenChange={setOpen}
              filterButtonLabel={filterButtonLabel}
              closeWithoutSave={closeWithoutSave}
              saveAndClose={() => {
                onChangePage?.(1);
                saveAndClose();
              }}
              levels={LEVELS}
              draftLevel={draftLevel}
              onDraftLevelChange={setDraftLevel}
              lastActiveOptions={lastActiveOptions}
              draftLastActive={draftLastActive}
              onDraftLastActiveChange={setDraftLastActive}
            />

            {/* TODO: need refactor, move logic to component sort and filter player */}
            <PlayerSortSheet
              open={sortOpen}
              onOpenChange={setSortOpen}
              sortButtonLabel={sortButtonLabel}
              closeWithoutSave={closeSortWithoutSave}
              saveAndClose={() => {
                onChangePage?.(1);
                saveSortAndClose();
              }}
              levelFilter={level}
              saved={{ key: sortKey, order: sortOrder }}
              draft={sortDraft}
              setDraft={setSortDraft}
            />
          </Stack>

          <Activity mode={resultSummary ? 'visible' : 'hidden'}>
            <Text
              fontFamily="Montserrat"
              fontSize={scale(8)}
              fontWeight="400"
              lineHeight={scale(10)}
              style={styles.resultsSummary}>
              {resultSummary}
            </Text>
          </Activity>
        </Stack>

        {totalPages && totalPages > 1 ? (
          <Stack
            width="100%"
            align="center"
            paddingX={scale({ phone: 18, tablet: 0 })}
            paddingBottom={scale(8)}
            paddingTop={scale(12)}>
            <Pagination
              currentPage={currentPage ?? 1}
              totalPages={totalPages}
              onPageChange={onChangePage}
            />
          </Stack>
        ) : (
          <Stack height={scale(4)} />
        )}
      </Animated.View>
      {extraComponent}
    </Stack>
  );
};
